import React, { useState, useEffect } from "react";
import { VendorService, UserService } from "src/services";
import { defaultMaxStartDateRangeDate, defaultStartDateRangeDate } from "src/helpers";
import {
  TransitPurchaseOrdersFilters,
  DateTimeRange,
  TransitStatus,
  MatchStatus,
  MatchAction,
  POCompletionStatus
} from "src/types";
import { CreationDateSortOptions, TransitPurchaseOrderColumnHeader } from "src/enums";
import { LoadingState } from "src/components";
import { DropdownField, MultiSelectInputBox } from "src/components/ui/inputs";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { Badge } from "src/shadcn/ui/badge";
import { AlertCircle, Trash2 } from "lucide-react";

interface FilterTransitPurchaseOrdersProps {
  onFilterChange: (filters: TransitPurchaseOrdersFilters) => void;
  currentFilter?: TransitPurchaseOrdersFilters;
  vendorService?: VendorService;
  userService?: UserService;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: TransitPurchaseOrderColumnHeader[];
  onColumnsToHideChange: (columns: TransitPurchaseOrderColumnHeader[]) => void;
  allColumns: TransitPurchaseOrderColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
}

export const FilterTransitPurchaseOrders: React.FC<FilterTransitPurchaseOrdersProps> = ({
  onFilterChange,
  currentFilter,
  vendorService,
  userService,
  dateTimeRange,
  onDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen
}) => {
  const [dropdownOptions, setDropdownOptions] = useState<{
    vendorNames?: string[];
    senderEmails?: string[];
  }>({});
  const [loadingDropdownOptions, setLoadingDropdownOptions] = useState(false);
  const [dropdownOptionsFetchError, setDropdownOptionsFetchError] = useState(false);

  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);

  const [filters, setFilters] = useState<Partial<TransitPurchaseOrdersFilters>>({
    transitStatusFilter: currentFilter?.transitStatusFilter || ["In Transit", "Partially Received"],
    vendorName: currentFilter?.vendorName,
    senderEmail: currentFilter?.senderEmail,
    matchStatusFilter: currentFilter?.matchStatusFilter,
    actionFilter: currentFilter?.actionFilter,
    completionStatusFilter: currentFilter?.completionStatusFilter,
    isBackorderFilter: currentFilter?.isBackorderFilter
  });

  // Reset local state to match applied filters when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setLocalDateTimeRange(dateTimeRange);
      setLocalSortOption(sortOption);
      setFilters({
        transitStatusFilter: currentFilter?.transitStatusFilter || ["In Transit", "Partially Received"],
        vendorName: currentFilter?.vendorName,
        senderEmail: currentFilter?.senderEmail,
        matchStatusFilter: currentFilter?.matchStatusFilter,
        actionFilter: currentFilter?.actionFilter,
        completionStatusFilter: currentFilter?.completionStatusFilter,
        isBackorderFilter: currentFilter?.isBackorderFilter
      });
    }
  }, [isOpen, dateTimeRange, sortOption, currentFilter]);

  useEffect(() => {
    fetchDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      setLoadingDropdownOptions(true);
      const [vendorNames, senderEmails] = await Promise.all([
        vendorService?.getVendorNames() ?? [],
        userService?.getUserEmails(["HEALF_OPERATIONS", "HEALF_OPERATIONS_MANAGER", "ADMIN"]) ?? []
      ]);

      setDropdownOptions({
        vendorNames,
        senderEmails
      });
    } catch (error) {
      setDropdownOptionsFetchError(true);
    } finally {
      setLoadingDropdownOptions(false);
    }
  };

  const handleFilterChange = <K extends keyof TransitPurchaseOrdersFilters>(
    key: K,
    value: TransitPurchaseOrdersFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const updatedFilters: TransitPurchaseOrdersFilters = {
      ...filters,
      filterPopoverVisible: false
    } as TransitPurchaseOrdersFilters;

    // Apply date range and sort changes
    onDateTimeRangeChange(localDateTimeRange);
    onSortChange(localSortOption);
    onFilterChange(updatedFilters);

    // Close the sidebar
    onClose?.();
  };

  const handleClearAllFilters = () => {
    const clearedFilters: TransitPurchaseOrdersFilters = {
      transitStatusFilter: ["In Transit", "Partially Received"],
      vendorName: undefined,
      senderEmail: undefined,
      matchStatusFilter: undefined,
      actionFilter: undefined,
      completionStatusFilter: undefined,
      isBackorderFilter: undefined,
      filterPopoverVisible: false
    };
    setFilters(clearedFilters);

    // Reset date range and sort to defaults
    const defaultRange = { start: defaultStartDateRangeDate(), end: undefined };
    setLocalDateTimeRange(defaultRange);
    onDateTimeRangeChange(defaultRange);

    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
  };

  const countActiveFilters = () => {
    let count = 0;
    // Check if transit status filter is different from default ["In Transit", "Partially Received"]
    const defaultTransitStatuses: TransitStatus[] = [
      "In Transit" as TransitStatus,
      "Partially Received" as TransitStatus
    ];
    const currentTransitStatuses = filters.transitStatusFilter || [];
    const isDefaultTransitStatus =
      currentTransitStatuses.length === defaultTransitStatuses.length &&
      currentTransitStatuses.every((status: TransitStatus) => defaultTransitStatuses.includes(status)) &&
      defaultTransitStatuses.every((status: TransitStatus) => currentTransitStatuses.includes(status));

    if (!isDefaultTransitStatus && currentTransitStatuses.length > 0) {
      count++;
    }
    if (filters.vendorName) count++;
    if (filters.senderEmail) count++;
    if (filters.matchStatusFilter && filters.matchStatusFilter.length > 0) count++;
    if (filters.actionFilter && filters.actionFilter.length > 0) count++;
    if (filters.completionStatusFilter && filters.completionStatusFilter.length > 0) count++;
    if (filters.isBackorderFilter !== undefined) count++;
    if (localDateTimeRange.start) {
      const defaultStart = defaultStartDateRangeDate();
      const isDefault =
        localDateTimeRange.start.getFullYear() === defaultStart.getFullYear() &&
        localDateTimeRange.start.getMonth() === defaultStart.getMonth() &&
        localDateTimeRange.start.getDate() === defaultStart.getDate() &&
        localDateTimeRange.start.getHours() === defaultStart.getHours() &&
        localDateTimeRange.start.getMinutes() === defaultStart.getMinutes();
      if (!isDefault) count++;
    }
    if (localDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (!value) {
      const newRange = {
        ...localDateTimeRange,
        [field]: undefined
      };
      setLocalDateTimeRange(newRange);
      return;
    }

    const newDate = new Date(value);
    const maxStartDate = defaultMaxStartDateRangeDate();
    const maxEndDate = new Date();

    if (field === "start") {
      // Start date should be >= maxStartDate (6 months ago) and <= end date or today
      const maxAllowedStart = localDateTimeRange.end ? new Date(localDateTimeRange.end) : maxEndDate;
      const clampedStart =
        newDate < maxStartDate ? maxStartDate : newDate > maxAllowedStart ? maxAllowedStart : newDate;

      setLocalDateTimeRange({
        start: clampedStart,
        end: localDateTimeRange.end && clampedStart > localDateTimeRange.end ? clampedStart : localDateTimeRange.end
      });
    } else {
      // End date should be >= start date and <= today
      const minAllowedEnd = localDateTimeRange.start ? new Date(localDateTimeRange.start) : maxStartDate;
      const clampedEnd = newDate < minAllowedEnd ? minAllowedEnd : newDate > maxEndDate ? maxEndDate : newDate;

      setLocalDateTimeRange({
        ...localDateTimeRange,
        end: clampedEnd
      });
    }
  };

  if (loadingDropdownOptions) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingState type="LOADING" />
      </div>
    );
  }

  if (dropdownOptionsFetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-sm text-secondary-600">Failed to load options</p>
        <Button onClick={fetchDropdownOptions} variant="outline" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters() > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters()}
              </Badge>
            )}
          </div>
          {countActiveFilters() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          <MultiSelectInputBox
            id="transit-status-filter"
            label="Transit Status"
            values={Array.isArray(filters.transitStatusFilter) ? filters.transitStatusFilter : []}
            onChange={(values) => handleFilterChange("transitStatusFilter", values as TransitStatus[])}
            options={["In Transit", "Partially Received", "Closed"] as TransitStatus[]}
            placeholder="Select transit statuses..."
          />

          <DropdownField
            label="Vendor"
            value={filters.vendorName}
            onChange={(value) => handleFilterChange("vendorName", value)}
            options={dropdownOptions.vendorNames || []}
          />

          <DropdownField
            label="Sender Email"
            value={filters.senderEmail}
            onChange={(value) => handleFilterChange("senderEmail", value)}
            options={dropdownOptions.senderEmails || []}
          />

          <MultiSelectInputBox
            id="match-status-filter"
            label="Match Status"
            values={Array.isArray(filters.matchStatusFilter) ? filters.matchStatusFilter : []}
            onChange={(values) => handleFilterChange("matchStatusFilter", values as MatchStatus[])}
            options={
              [
                "MATCHED",
                "DISCREPANCY",
                "NO_CHECKINS",
                "POTENTIAL_MATCH_FOUND",
                "UNMATCHED_SHORTAGE",
                "OVERAGE"
              ] as MatchStatus[]
            }
            placeholder="Select match statuses..."
          />

          <MultiSelectInputBox
            id="action-filter"
            label="Action"
            values={Array.isArray(filters.actionFilter) ? filters.actionFilter : []}
            onChange={(values) => handleFilterChange("actionFilter", values as MatchAction[])}
            options={
              [
                "NO_ACTION",
                "REVIEW",
                "INVESTIGATE",
                "INVESTIGATE: Missing check-in",
                "REVIEW: Overage"
              ] as MatchAction[]
            }
            placeholder="Select actions..."
          />

          <MultiSelectInputBox
            id="completion-status-filter"
            label="Completion Status"
            values={Array.isArray(filters.completionStatusFilter) ? filters.completionStatusFilter : []}
            onChange={(values) => handleFilterChange("completionStatusFilter", values as POCompletionStatus[])}
            options={["Draft", "Scheduled", "In Progress", "Complete", "Cancelled"] as POCompletionStatus[]}
            placeholder="Select completion statuses..."
          />

          <DropdownField
            label="PO Type"
            value={
              filters.isBackorderFilter === true
                ? "Backorder"
                : filters.isBackorderFilter === false
                  ? "Normal"
                  : undefined
            }
            onChange={(value) => {
              if (value === "Backorder") {
                handleFilterChange("isBackorderFilter", true);
              } else if (value === "Normal") {
                handleFilterChange("isBackorderFilter", false);
              } else {
                handleFilterChange("isBackorderFilter", undefined);
              }
            }}
            options={["Backorder", "Normal"]}
          />

          {/* Creation Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - Start
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.start)}
              onChange={(e) => handleDateChange("start", e.target.value)}
              min={formatDateForInput(defaultMaxStartDateRangeDate())}
              max={formatDateForInput(localDateTimeRange.end || new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - End
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.end)}
              onChange={(e) => handleDateChange("end", e.target.value)}
              min={formatDateForInput(localDateTimeRange.start || defaultMaxStartDateRangeDate())}
              max={formatDateForInput(new Date())}
              className="text-sm h-9"
            />
          </div>

          {/* Sort */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>

        {/* Apply Button */}
        <div className="sticky -bottom-4 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
          >
            Apply Changes
          </Button>
        </div>
      </div>

      {/* Column Visibility */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible
                    ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100"
                    : "bg-transparent text-secondary-400 hover:bg-secondary-50"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge
                  variant={isVisible ? "default" : "outline"}
                  className={`text-xs px-1.5 py-0 h-4 flex-shrink-0 ml-2 ${isVisible ? "bg-green-500 hover:bg-green-600" : ""}`}
                >
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
